import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode
} from 'react';
import { useAuth, switchCompanyWorkspace } from '@futuredev/shared-auth';

interface ActiveCompanyState {
  companyId: number | null;
  companyName: string | null;
}

interface ActiveCompanyContextValue extends ActiveCompanyState {
  setActiveCompany: (id: number, name: string) => Promise<void>;
  clearActiveCompany: () => Promise<void>;
  switching: boolean;
}

const STORAGE_KEY = 'futuredev_active_company';

function loadFromStorage(): ActiveCompanyState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ActiveCompanyState;
      if (parsed.companyId && typeof parsed.companyId === 'number') {
        return parsed;
      }
    }
  } catch {
    // corrupt storage — ignore
  }
  return { companyId: null, companyName: null };
}

const ActiveCompanyContext = createContext<ActiveCompanyContextValue | null>(null);

export function ActiveCompanyProvider({ children }: { children: ReactNode }) {
  const { user, checkAuth } = useAuth();
  const [state, setState] = useState<ActiveCompanyState>(loadFromStorage);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (state.companyId) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [state]);

  useEffect(() => {
    const jwtCompany = user?.activeCompanyId ?? user?.companyId ?? user?.company_id ?? null;
    if (!jwtCompany) {
      return;
    }
    setState((prev) => {
      if (prev.companyId === jwtCompany) {
        return prev;
      }
      return {
        companyId: jwtCompany,
        companyName: `Company #${jwtCompany}`,
      };
    });
  }, [user?.activeCompanyId, user?.companyId, user?.company_id]);

  const setActiveCompany = useCallback(
    async (id: number, name: string) => {
      try {
        setSwitching(true);
        const resolved = await switchCompanyWorkspace(id);
        await checkAuth();
        setState({ companyId: resolved ?? id, companyName: name });
      } catch (err) {
        console.error('Failed to switch company context:', err);
        throw err;
      } finally {
        setSwitching(false);
      }
    },
    [checkAuth]
  );

  const clearActiveCompany = useCallback(async () => {
    try {
      setSwitching(true);
      const resolved = await switchCompanyWorkspace(null);
      await checkAuth();
      setState({
        companyId: resolved,
        companyName: resolved ? 'Personal workspace' : null,
      });
    } catch (err) {
      console.error('Failed to clear company context:', err);
      throw err;
    } finally {
      setSwitching(false);
    }
  }, [checkAuth]);

  return (
    <ActiveCompanyContext.Provider
      value={{
        companyId: state.companyId,
        companyName: state.companyName,
        setActiveCompany,
        clearActiveCompany,
        switching
      }}
    >
      {children}
    </ActiveCompanyContext.Provider>
  );
}

export function useActiveCompany(): ActiveCompanyContextValue {
  const ctx = useContext(ActiveCompanyContext);
  if (!ctx) {
    return {
      companyId: null,
      companyName: null,
      setActiveCompany: async () => {},
      clearActiveCompany: async () => {},
      switching: false
    };
  }
  return ctx;
}
